import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import classNames from "clsx";

import Col from "react-bootstrap/Col";
import Modal, { ModalProps } from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import InfiniteScroll from "react-infinite-scroll-component";

import { usePartogramFetus } from "@/hooks/usePartogramFetus";
import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { FetusModalStatus } from "@/enums/FetusModalStatus";

import { KTSVG } from "@/../_metronic/helpers";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";

type FillingHistoryModalProps = ModalProps & {
	fetusId: number;
	fetusIndex: number;
	openAddFetusModal: () => void;
	fillingHistory: {
		data: PaginatedFillingHistory;
		fetch: (pageToUse?: number) => Promise<void>;
		isLoading: boolean;
		setIsLoading: (value: boolean) => void;
	};
};

export function FillingHistoryModal({
	fetusId,
	fetusIndex,
	openAddFetusModal,
	fillingHistory,
	...rest
}: FillingHistoryModalProps) {
	const { width } = useWindowDimensions();
	const { setFetusModalStatus, setCurrentPartogramDataId, setCurrentPartogramFetusId } =
		usePartogramFetus();

	const [page, setPage] = useState(1);

	const forcePage = useRef(1);

	const results = fillingHistory.data.results;
	const dataLength = results?.length || 1;

	function fillingHistorySkeleton() {
		const arrayOfLengthFour = Array.from({ length: 4 });

		return arrayOfLengthFour.map((_, index) => (
			<Col lg={12} key={index} className="mb-5">
				<SkeletonWrapper
					containerClassName="d-flex gap-4"
					width={width <= 991 ? "100%" : "50%"}
					height={100}
					count={width <= 991 ? 1 : 2}
					inline
				/>
			</Col>
		));
	}

	function handleNextPage() {
		if (forcePage.current !== page) {
			forcePage.current += 1;
			fillingHistory.fetch(forcePage.current);
			return;
		}

		setPage(page + 1);
	}

	useEffect(() => {
		forcePage.current = page;

		fillingHistory.fetch(page);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	useEffect(() => {
		fillingHistory.setIsLoading(true);

		forcePage.current = 1;
		fillingHistory.fetch(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetusId]);

	function closeFillingHistoryModalAndOpenAddFetusModal() {
		rest.onHide?.();

		setTimeout(openAddFetusModal, 15);
	}

	function setupFetusModal(partogramDataId: number, partogramFetusId: number) {
		setCurrentPartogramDataId(partogramDataId);
		setCurrentPartogramFetusId(partogramFetusId);
		closeFillingHistoryModalAndOpenAddFetusModal();
	}

	function handleClickToViewFilling(partogramDataId: number, partogramFetusId: number) {
		setFetusModalStatus(FetusModalStatus.Viewing);
		setupFetusModal(partogramDataId, partogramFetusId);
	}

	function handleClickToEditFilling(partogramDataId: number, partogramFetusId: number) {
		setFetusModalStatus(FetusModalStatus.Editing);
		setupFetusModal(partogramDataId, partogramFetusId);
	}

	return (
		<Modal size="lg" {...rest}>
			<Modal.Header closeButton>
				<span className="btn-close invisible" /> {/* this is to center modal title */}
				<Modal.Title>Histórico Partograma - Feto {fetusIndex}</Modal.Title>
			</Modal.Header>

			<Modal.Body className="px-10">
				<InfiniteScroll
					className={classNames(
						"d-flex align-items-start justify-content-between pe-5",
						width <= 991 ? "flex-column" : "flex-wrap"
					)}
					height="57vh"
					dataLength={dataLength}
					next={handleNextPage}
					hasMore={
						Boolean(results?.length) &&
						results?.length !== fillingHistory.data.meta?.total
					}
					loader={fillingHistorySkeleton()}
				>
					{fillingHistory.isLoading
						? fillingHistorySkeleton()
						: results?.map((filling, index) => (
								<div
									key={index}
									className="d-flex justify-content-between align-items-center border-bottom-dotted border-gray-200 pb-5 mb-5"
									style={{ width: width <= 991 ? "100%" : "48%" }}
								>
									<Col>
										<p className="text-gray-500 fs-5 mb-0">
											{filling.created_at
												? dayjs(filling.created_at).format("DD/MM/YYYY")
												: "Não informado"}
										</p>
										<p className="fw-bold fs-4 mb-2">
											{filling.created_at
												? dayjs(filling.created_at).format("HH:mm")
												: "-"}
										</p>
										<p className="fw-bolder fs-6 mb-1">
											{filling.last_modifier.name}
										</p>
										<p className="text-gray-500 fs-5 mb-0">
											CBO: {filling.last_modifier.bco}
										</p>
									</Col>

									<Col className="d-flex gap-2 justify-content-end">
										<Button
											variant="primary"
											className="btn-icon btn-icon-only"
											onClick={() =>
												handleClickToViewFilling(
													filling.id,
													filling.partogram_fetus_id
												)
											}
										>
											<KTSVG
												path="/media/icons/buttons/eye.svg"
												className="svg-icon-2"
											/>
										</Button>

										<Button
											variant="secondary"
											className="btn-icon btn-icon-only"
											onClick={() =>
												handleClickToEditFilling(
													filling.id,
													filling.partogram_fetus_id
												)
											}
										>
											<KTSVG
												path="/media/icons/buttons/pencil.svg"
												className="svg-icon-2"
											/>
										</Button>
									</Col>
								</div>
						  ))}

					{!results?.length && fillingHistorySkeleton()}
				</InfiniteScroll>
			</Modal.Body>

			<Modal.Footer className="px-10 border-0">
				<Button
					variant="secondary"
					className="hover-elevate-up px-10"
					onClick={rest.onHide}
				>
					Sair
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
